import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  IDataService,
  IDataResponse,
  IParsedDataResponse,
  LANGUAGE_CONSTANT,
  IAvailableLanguage,
  ICurrency,
} from './data-service.interface';
import { HttpClient } from '@angular/common/http';
import { IEnvironment } from '../environment/environment.interface';
import { map } from 'rxjs/operators';
import { DataServiceConstants } from './data.constants';
import { StorageMap } from '@ngx-pwa/local-storage';
import { BrowserWindowRef } from '../window/window.service';
import {
  COUNTRIES_TRANSLATIONS,
  EARNING_STATUS_TRANSLATIONS,
  OFFER_REQUEST_STATUSES_TRANSLATIONS,
  POSTBACK_STATUS_TRANSLATIONS,
} from './data-constants-translations';

@Injectable({
  providedIn: 'root',
})
export class DataService implements IDataService {
  private dataApi: string;
  private data: any;
  private dataServiceConstants = DataServiceConstants;

  constructor(
    @Inject('IEnvironmentService') private envService: IEnvironment,
    private http: HttpClient,
    @Inject(StorageMap) private localStorage: StorageMap,
    @Inject(BrowserWindowRef) private window: BrowserWindowRef
  ) {
    this.dataApi = this.envService.getVar('services.api') as string;
    this.localStorage.delete('column_visibility_AffiliateCrm\\OffersTabletable').subscribe(() => {});
  }

  public setData(data: any): void {
    this.data = data;
  }

  public getDataFromServer(): Observable<IParsedDataResponse> {
    return this.http.get(`${this.dataApi}/data`).pipe(
      map((r: IDataResponse) => {
        const data: IParsedDataResponse = {
          version: 'local',
          countries: {},
          countriesWithoutAll: {},
          timezones: {},
          advertisers: {},
          offers: {},
          offers_full: {},
          goals: {},
          offer_goals: {},
          permissions: [],
          roles: {},
          tracking_domains: {},
          invoice_currency_codes: [],
          invoice_statuses: [],
          months: {},
          payout_type: { 1: 'Cost per action (CPA)' },
          revenue_type: { 1: 'Revenue per action (RPA)' },
          conversion_tracking_type: {
            0: $localize`:@@conversion.postback_with_click_id:Postback with Click ID`,
            1: 'iFrame pixel',
          },
          affiliate_tracker_type: {
            0: 'Postback',
            1: 'iFrame pixel',
          },
          goal_types: {},
          terms: {},
          languages: {},
          affiliate_trackers_placeholder: [],
          postback_status: {},
          default_currency: {},
          kyc_states: {},
          kyc_types: {},
          kyc_approve_modes: {},
          offer_banners: {},
          all_languages: {},
          finance_earning_statuses: {},
          finance_invoice_statuses: {},
          finance_payment_statuses: {},
          user_id: 0,
          payment_systems: [],
          payment_systems_list: {},
          currency_list: {},
          referral_programs: [],
          payout_models: {},
          default_currencies: {},
          available_languages: {},
          current_language: LANGUAGE_CONSTANT.EN,
          offer_request_statuses: this.translateConstants(r.data.offer_requests_statuses, OFFER_REQUEST_STATUSES_TRANSLATIONS),
          isUnpaid: false,
          available_offer_currencies: [],
          offer_currencies_options: this.getCurrencyOptions(
            r.data?.available_offer_currencies.filter((code) => Boolean(code)) || [],
            r.data?.currency_list
          ),
          referralBannerEnabled: true,
        };

        data.available_languages = this.dataServiceConstants.availableLanguages;
        data.current_language = this.getCurrentLanguage(data.available_languages);

        data.countries = this.translateConstants(this.dataServiceConstants.countries, COUNTRIES_TRANSLATIONS, 'name');
        data.countriesWithoutAll = (() => {
          const countries = JSON.parse(JSON.stringify(data.countries));
          delete countries['**'];
          return countries;
        })();

        for (const i in this.dataServiceConstants.timezones) {
          if (Object.prototype.hasOwnProperty.call(this.dataServiceConstants.timezones, i)) {
            data.timezones[i] = this.dataServiceConstants.timezones[i];
          }
        }

        data.version = r.data.version;
        data.advertisers = r.data.advertisers;
        data.permissions = r.data.permissions;
        data.roles = r.data.roles;
        data.tracking_domains = r.data.tracking_domains;
        data.invoice_currency_codes = r.data.invoice_currency_codes;
        data.payment_systems =
          r.data.payment_systems?.map((paymentSystem) => {
            const codes = this.prepareCurrencies(paymentSystem.currencies ?? [], r?.data?.currency_list || {});

            return { name: paymentSystem.name, codes };
          }) ?? [];
        data.invoice_statuses = r.data.invoice_statuses;
        data.finance_earning_statuses = this.translateConstants(r.data.finance_earning_statuses, EARNING_STATUS_TRANSLATIONS);
        data.finance_invoice_statuses = r.data.finance_invoice_statuses;
        data.finance_payment_statuses = r.data.finance_payment_statuses;
        data.goal_types = r.data.goal_types;
        data.months = months;
        data.terms = r.data.terms;
        data.affiliate_trackers_placeholder = r.data.affiliate_trackers_placeholder;

        data.offers_full = r.data.offers;
        for (const offerId of Object.keys(r.data.offers)) {
          const offer = r.data.offers[offerId];
          data.offers[offerId] = offer.name;
          data.offer_goals[offerId] = {};
          data.offer_banners[offerId] = offer.width_height || [];

          for (const goalId of Object.keys(offer.goals)) {
            data.goals[goalId] = offer.goals[goalId];
            data.offer_goals[offerId][goalId] = offer.goals[goalId];
          }
        }

        data.payout_type = Object.entries(r?.data?.payout_models || {}).reduce((acc, value) => {
          acc[value[0]] = value[1]?.name;
          return acc;
        }, {});
        data.revenue_type = Object.entries(r?.data?.revenue_models || {}).reduce((acc, value) => {
          acc[value[0]] = value[1]?.name;
          return acc;
        }, {});
        data.languages = this.dataServiceConstants.languages;
        data.all_languages = JSON.parse(JSON.stringify(this.dataServiceConstants.languages));
        data.all_languages['**'] = { code: '**', name: $localize`:@@common.all_languages:All Languages`, img: '' };
        data.postback_status = this.translateConstants(r?.data?.postback_status, POSTBACK_STATUS_TRANSLATIONS);
        data.default_currency = r?.data?.default_currency;
        data.kyc_states = r?.data?.kyc_states;
        data.kyc_types = r?.data?.kyc_types;
        data.kyc_approve_modes = r?.data?.kyc_approve_modes;
        data.payment_systems_list = r?.data?.payment_systems_list;
        data.currency_list = r?.data?.currency_list;
        data.referral_programs = r?.data?.referral_programs;
        data.payout_models = r?.data?.payout_models;
        data.default_currencies = r?.data?.default_currencies;
        data.isUnpaid = r?.data?.isUnpaid;
        data.available_offer_currencies = r?.data?.available_offer_currencies || [];
        data.referralBannerEnabled = r?.data?.referralBannerEnabled ?? true;

        for (const key in r?.data) {
          if (!(key in data)) {
            data[key] = r?.data[key];
          }
        }

        return data;
      })
    );
  }

  private prepareCurrencies(currencies: string[], currencyList: ICurrency): Record<string, any> {
    return currencies.reduce((list, currency) => {
      list[currency] = currencyList[currency];
      return list;
    }, {});
  }

  getData(): any {
    return this.data;
  }

  public getDataConstants(): object {
    return this.dataServiceConstants;
  }

  private getCurrentLanguage(availableLanguages: Record<string, IAvailableLanguage>): string {
    const currentLanguage = this.window.nativeWindow.location.pathname.split('/')[2] ?? LANGUAGE_CONSTANT.EN;
    return availableLanguages[currentLanguage] ? currentLanguage : LANGUAGE_CONSTANT.EN;
  }

  private translateConstants<T>(constants: T, translations: Record<keyof T, string>, field?: string): T {
    Object.keys(constants).map((key) => {
      if (field) {
        constants[key][field] = translations[key] || constants[key][field];
      } else {
        constants[key] = translations[key] || constants[key];
      }
    });
    return constants;
  }
  private getCurrencyOptions(codes: string[], currencyList: ICurrency): ICurrency {
    return codes.reduce((options, code) => {
      options[code] = currencyList[code];
      return options;
    }, {});
  }
}

const months = {
  0: 'January',
  1: 'February',
  2: 'March',
  3: 'April',
  4: 'May',
  5: 'June',
  6: 'July',
  7: 'August',
  8: 'September',
  9: 'October',
  10: 'November',
  11: 'December',
};
